import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  TrendingUp, Users, Clock, ChefHat, Receipt, DollarSign, Activity,
  RefreshCw, Lock, LogOut, Utensils, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MANAGER_PASSWORD = 'Manager123';
const ADMIN_PASSWORD = 'Admin123';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-[#E5DCD3] px-3 py-2">
      <p className="text-xs font-bold text-[#2D241D]">{label}h</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color }}>
          {p.name}: <strong>{p.value?.toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

export default function ManagerPage() {
  const [isAuth, setIsAuth] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topProducts, setTopProducts] = useState([]);
  const sseRef = useRef(null);

  const handleLogin = () => {
    if (password === MANAGER_PASSWORD || password === ADMIN_PASSWORD) {
      setIsAuth(true);
      sessionStorage.setItem('managerAuth', 'true');
    } else {
      setError('Mot de passe incorrect');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('managerAuth') === 'true') setIsAuth(true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [liveRes, topRes] = await Promise.allSettled([
        axios.get(`${API}/admin/dashboard/live`),
        axios.get(`${API}/admin/analytics-advanced/top-products`, { params: { period: '1d', limit: 8 } }),
      ]);
      if (liveRes.status === 'fulfilled') setData(liveRes.value.data);
      if (topRes.status === 'fulfilled') setTopProducts(topRes.value.data);
    } catch { /* */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isAuth) return;
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [isAuth, fetchData]);

  // SSE for real-time updates
  useEffect(() => {
    if (!isAuth) return;
    const es = new EventSource(`${API}/events/admin`);
    sseRef.current = es;
    es.onmessage = () => fetchData();
    es.onerror = () => { es.close(); setTimeout(() => {
      if (isAuth) { sseRef.current = new EventSource(`${API}/events/admin`); }
    }, 5000); };
    return () => es.close();
  }, [isAuth, fetchData]);

  const handleLogout = () => {
    setIsAuth(false);
    setPassword('');
    sessionStorage.removeItem('managerAuth');
  };

  if (!isAuth) {
    return (
      <div className="h-screen w-screen bg-[#1A1A1A] flex items-center justify-center" data-testid="manager-login-screen">
        <div className="bg-white rounded-3xl p-10 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#6366F1] rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-black text-[#2D241D]">DASHBOARD MANAGER</h1>
            <p className="text-[#6E5E52] mt-2 text-sm">Statistiques temps reel</p>
          </div>
          {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-4 text-center text-sm font-medium">{error}</div>}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Mot de passe manager"
            className="w-full h-14 text-xl text-center tracking-widest rounded-2xl border-2 border-[#E5DCD3] bg-[#FAF7F2] text-[#2D241D] focus:border-[#6366F1] focus:outline-none px-6 mb-4"
            data-testid="manager-password-input" />
          <button onClick={handleLogin}
            className="w-full h-14 bg-[#6366F1] hover:bg-[#5558E6] text-white text-lg font-bold rounded-2xl transition-colors active:scale-[0.98]"
            data-testid="manager-login-btn">
            Connexion
          </button>
        </div>
      </div>
    );
  }

  const fp = (p) => p?.toLocaleString() || '0';

  const hourlyData = data?.hourly_revenue
    ? Object.entries(data.hourly_revenue)
        .map(([h, v]) => ({ hour: `${h}h`, revenue: Math.round(v) }))
        .sort((a, b) => parseInt(a.hour) - parseInt(b.hour))
    : [];

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white" data-testid="manager-dashboard">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-[#2A2A2A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-[#6366F1]" />
          <h1 className="text-xl font-black tracking-wide">DASHBOARD LIVE</h1>
          <div className="flex items-center gap-1.5 bg-red-500/20 text-red-400 px-2.5 py-1 rounded-full text-xs font-bold">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            EN DIRECT
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-white/40">
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
          <button onClick={fetchData} className="p-2 rounded-lg bg-[#2A2A2A] hover:bg-[#3A3A3A] transition-colors">
            <RefreshCw className="w-4 h-4 text-white/60" />
          </button>
          <button onClick={handleLogout} className="p-2 rounded-lg bg-[#2A2A2A] hover:bg-[#3A3A3A] transition-colors"
            data-testid="manager-logout-btn">
            <LogOut className="w-4 h-4 text-white/60" />
          </button>
        </div>
      </header>

      {loading || !data ? (
        <div className="flex items-center justify-center h-[80vh]">
          <div className="w-10 h-10 border-4 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-[#6366F1] to-[#4F46E5] rounded-2xl p-5" data-testid="manager-kpi-revenue">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-white/70" />
                <span className="text-sm text-white/70">CA du jour</span>
              </div>
              <p className="text-3xl font-black">{fp(data.total_revenue)}</p>
              <p className="text-xs text-white/50 mt-1">{data.paid_orders} payes / {data.total_orders} total</p>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-5" data-testid="manager-kpi-orders">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-[#D4A853]" />
                <span className="text-sm text-white/50">Commandes actives</span>
              </div>
              <p className="text-3xl font-black text-[#D4A853]">{data.active_orders}</p>
              <div className="flex gap-3 mt-1 text-xs text-white/40">
                <span>{data.pending_kitchen} en attente</span>
                <span>{data.preparing} en cours</span>
              </div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-5" data-testid="manager-kpi-tables">
              <div className="flex items-center gap-2 mb-2">
                <Utensils className="w-5 h-5 text-[#2D6A4F]" />
                <span className="text-sm text-white/50">Tables occupees</span>
              </div>
              <p className="text-3xl font-black text-[#2D6A4F]">{data.active_tables}</p>
              <p className="text-xs text-white/40 mt-1">
                {data.total_orders > 0 ? `Ticket moyen: ${fp(Math.round(data.total_revenue / data.paid_orders || 1))}` : 'Aucune commande'}
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-5" data-testid="manager-kpi-prep">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-[#E16833]" />
                <span className="text-sm text-white/50">Temps moyen prep.</span>
              </div>
              <p className="text-3xl font-black text-[#E16833]">{data.avg_prep_time_min} min</p>
              <p className="text-xs text-white/40 mt-1">Depuis reception</p>
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hourly revenue chart */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden" data-testid="manager-hourly-chart">
              <div className="px-5 py-4 border-b border-[#2A2A2A] flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#6366F1]" />
                <h3 className="text-sm font-bold">CA par heure</h3>
              </div>
              <div className="p-4 h-[280px]">
                {hourlyData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyData}>
                      <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#666' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#666' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" name="CA" radius={[4, 4, 0, 0]}>
                        {hourlyData.map((entry, i) => (
                          <Cell key={i} fill={entry.revenue > 0 ? '#6366F1' : '#2A2A2A'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-white/30 text-sm">
                    Pas encore de donnees aujourd'hui
                  </div>
                )}
              </div>
            </div>

            {/* Top products */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden" data-testid="manager-top-products">
              <div className="px-5 py-4 border-b border-[#2A2A2A] flex items-center gap-2">
                <ChefHat className="w-4 h-4 text-[#D4A853]" />
                <h3 className="text-sm font-bold">Top ventes du jour</h3>
              </div>
              <div className="divide-y divide-[#2A2A2A]">
                {topProducts.length === 0 ? (
                  <div className="px-5 py-8 text-center text-white/30 text-sm">Pas encore de ventes</div>
                ) : topProducts.map((p, i) => (
                  <div key={p.name} className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-[#D4A853] text-black' : i < 3 ? 'bg-[#6366F1]/20 text-[#6366F1]' : 'bg-[#2A2A2A] text-white/50'
                      }`}>{i + 1}</span>
                      <span className="text-sm font-medium">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-white/50">{p.quantity} vendus</span>
                      <span className="font-bold text-[#D4A853]">{fp(p.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
